"use client";

import Image from "next/image";
import Link from "next/link";
import { motion, useReducedMotion, useMotionValue, useTransform, useSpring } from "motion/react";
import type { Variants } from "motion/react";
import { useCallback } from "react";
import { AedSymbol } from "@/components/ui/AedSymbol";
import { DownloadAppLink } from "@/components/ui/DownloadAppLink";
import { buttonVariants } from "@/components/ui/button";
import { BadgeCheck, Coins, Package, ArrowRightLeft } from "lucide-react";

// ── Variants ─────────────────────────────────────────────────────────────────

const stagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.14, delayChildren: 0.08 } },
};

function fadeUp(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, y: reduce ? 0 : 22 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: [0.2, 0, 0, 1] },
    },
  };
}

const headlineStagger: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.16, delayChildren: 0.12 } },
};

function revealLine(reduce: boolean): Variants {
  return {
    hidden: {
      opacity: 0,
      y: reduce ? 0 : "112%",
      filter: reduce ? "none" : "blur(8px)",
    },
    visible: {
      opacity: 1,
      y: 0,
      filter: "blur(0px)",
      transition: { duration: 0.78, ease: [0.2, 0, 0, 1] },
    },
  };
}

function slideIn(reduce: boolean): Variants {
  return {
    hidden: { opacity: 0, x: reduce ? 0 : 40 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.8, ease: [0.2, 0, 0, 1], delay: 0.2 },
    },
  };
}

// ── Glass chip data ──────────────────────────────────────────────────────────

const GLASS_CHIPS = [
  {
    icon: Coins,
    text: "Saved 2g of gold",
    delay: 0.6,
    offset: "ml-14",
  },
  {
    icon: Package,
    text: "10g gold bar delivered",
    delay: 0.85,
    offset: "-ml-2",
  },
  {
    icon: ArrowRightLeft,
    text: "Sold silver, withdrew to bank",
    delay: 1.1,
    offset: "ml-2",
  },
];

// Each chip moves at a different intensity for a parallax layered feel
const PARALLAX_FACTORS = [
  { x: 18, y: 14 },
  { x: -12, y: 18 },
  { x: 22, y: -10 },
];

function GlassChip({
  icon: Icon,
  text,
  delay,
  offset,
  index,
  reduce,
  mouseX,
  mouseY,
}: {
  icon: React.ElementType;
  text: string;
  delay: number;
  offset: string;
  index: number;
  reduce: boolean;
  mouseX: ReturnType<typeof useMotionValue<number>>;
  mouseY: ReturnType<typeof useMotionValue<number>>;
}) {
  const factor = PARALLAX_FACTORS[index];
  const springConfig = { stiffness: 120, damping: 25, mass: 0.8 };

  const rawX = useTransform(mouseX, [-1, 1], [-factor.x, factor.x]);
  const rawY = useTransform(mouseY, [-1, 1], [-factor.y, factor.y]);
  const x = useSpring(rawX, springConfig);
  const y = useSpring(rawY, springConfig);

  return (
    <motion.div
      initial={{ opacity: 0, scale: reduce ? 1 : 0.9, filter: "blur(4px)" }}
      animate={{ opacity: 1, scale: 1, filter: "blur(0px)" }}
      transition={{ duration: 0.6, ease: [0.2, 0, 0, 1], delay }}
      style={reduce ? {} : { x, y }}
      className={`flex items-center gap-2.5 px-4 py-2.5 w-fit
        rounded-card
        bg-white/60 backdrop-blur-xl
        border border-white/80
        shadow-[0_4px_24px_rgba(0,0,0,0.06),0_1px_3px_rgba(0,0,0,0.04)]
        ${offset}`}
    >
      <span className="flex items-center justify-center w-7 h-7 rounded-lg
        bg-primary-600/10">
        <Icon className="w-3.5 h-3.5 text-primary-600" strokeWidth={2} />
      </span>
      <span className="text-sm font-medium text-primary-800 whitespace-nowrap">
        {text}
      </span>
    </motion.div>
  );
}

// ── HeroSection ───────────────────────────────────────────────────────────────

export function HeroSection() {
  const reduce = useReducedMotion() ?? false;

  // Mouse tracking for parallax (-1 to 1 normalized)
  const mouseX = useMotionValue(0);
  const mouseY = useMotionValue(0);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      const rect = e.currentTarget.getBoundingClientRect();
      const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      const ny = ((e.clientY - rect.top) / rect.height) * 2 - 1;
      mouseX.set(nx);
      mouseY.set(ny);
    },
    [mouseX, mouseY]
  );

  const handleMouseLeave = useCallback(() => {
    mouseX.set(0);
    mouseY.set(0);
  }, [mouseX, mouseY]);

  return (
    <section
      className="relative w-full overflow-hidden bg-neutral-0"
      aria-labelledby="hero-heading"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
    >
      {/* ── Background decorative image (full width, behind everything) ─── */}
      <Image
        src="/hero-bg.png"
        alt=""
        width={1975}
        height={796}
        priority
        className="pointer-events-none absolute inset-0 h-full w-full
          object-cover object-center select-none"
        aria-hidden="true"
      />

      {/* ── Top fade: white → transparent gradient overlay ─────────────── */}
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-[1] h-[160px]
          bg-gradient-to-b from-neutral-0 via-neutral-0/70 to-transparent"
        aria-hidden="true"
      />

      {/* ── Content ────────────────────────────────────────────────────────── */}
      <div className="relative z-10 mx-auto max-w-[1440px]
        grid grid-cols-1 lg:grid-cols-2 items-end
        min-h-[420px] lg:min-h-[580px]">

        {/* LEFT — Text column */}
        <motion.div
          variants={stagger}
          initial="hidden"
          animate="visible"
          className="flex flex-col items-center lg:items-start gap-5
            px-6 sm:px-10 lg:pl-20 lg:pr-8
            py-16 lg:py-0 lg:self-center
            text-center lg:text-left"
        >
          {/* Social-proof line */}
          <motion.div
            variants={fadeUp(reduce)}
            className="inline-flex max-w-full items-center gap-3 py-1 pr-1"
          >
            <span
              className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-accent-100 text-accent-600"
              aria-hidden="true"
            >
              <BadgeCheck className="h-4 w-4" strokeWidth={2} />
            </span>
            <span className="flex min-w-0 flex-nowrap items-center gap-x-1.5 text-left leading-none sm:gap-x-2">
              <span className="inline-flex shrink-0 items-center gap-1 text-sm font-semibold text-accent-500 sm:gap-1.5 sm:text-lg">
                <AedSymbol className="h-[1.05em] w-[1.05em]" label="AED " />
                21M+
              </span>
              <span className="truncate text-[11px] font-medium text-primary-600 sm:text-[15px]">
                in gold &amp; silver sold on JustGold
              </span>
            </span>
          </motion.div>

          {/* Headline */}
          <motion.h1
            id="hero-heading"
            variants={headlineStagger}
            className="text-[clamp(2.25rem,4.5vw,3.5rem)] font-semibold leading-[1.1]
              tracking-[-0.015em] text-primary-800"
          >
            <span className="block overflow-hidden pb-1">
              <motion.span variants={revealLine(reduce)} className="block">
                Your{" "}
                <span className="font-instrument italic font-normal">family</span>{" "}
                has always
              </motion.span>
            </span>
            <span className="block overflow-hidden pb-1">
              <motion.span variants={revealLine(reduce)} className="block">
                saved in gold.
              </motion.span>
            </span>
          </motion.h1>

          {/* Entry point sits below the promise, not inside the headline. */}
          <motion.p
            variants={fadeUp(reduce)}
            className="font-heading text-[clamp(1.4rem,2.6vw,2rem)] font-semibold leading-[1.1] tracking-[-0.015em] text-primary-800"
          >
            Now start with{" "}
            <span className="inline-flex items-baseline gap-1 text-accent-500">
              <AedSymbol className="h-[0.72em] w-[0.83em] align-baseline" label="AED " />
              {" "}10.
            </span>
          </motion.p>

          {/* Body copy */}
          <motion.p
            variants={fadeUp(reduce)}
            className="max-w-[520px] text-lg leading-relaxed
              text-text-body"
          >
            Real 24K gold and 999 silver, bought from your phone. Stored in
            insured Dubai vaults in your name. Sell it, cash out to your
            bank, or have it delivered to your door whenever you want.
          </motion.p>

          {/* CTA */}
          <motion.div
            variants={fadeUp(reduce)}
            className="flex flex-wrap items-center gap-3"
          >
            <DownloadAppLink className={buttonVariants({ variant: "brand", size: "md" })}>
              Start from <AedSymbol className="h-[0.72em] w-[0.83em] align-baseline" label="AED " />
              {" "}10
            </DownloadAppLink>
            <Link
              href="/gold-silver-prices"
              className={buttonVariants({ variant: "outline-brand", size: "md" })}
            >
              See today&apos;s rate
            </Link>
          </motion.div>
        </motion.div>

        {/* RIGHT — Person image (bottom-anchored) + Glass chips */}
        <motion.div
          variants={slideIn(reduce)}
          initial="hidden"
          animate="visible"
          className="relative flex justify-center lg:justify-end items-end self-end
            px-6 lg:px-0"
        >
          {/* Glass chips — grouped stack */}
          <div
            className="absolute z-20 hidden lg:flex flex-col gap-3
              right-[6%] bottom-[10%]"
            aria-hidden="true"
          >
            {GLASS_CHIPS.map((chip, i) => (
              <GlassChip key={chip.text} {...chip} index={i} reduce={reduce} mouseX={mouseX} mouseY={mouseY} />
            ))}
          </div>

          <Image
            src="/hero-person-2.png"
            alt="Person in white thobe smiling while using the JustGold app on their phone"
            width={1402}
            height={1122}
            priority
            className="h-[300px] sm:h-[380px] lg:h-[520px] xl:h-[560px] w-auto
              object-contain object-bottom select-none pointer-events-none"
          />
        </motion.div>
      </div>
    </section>
  );
}
